import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { identifyFullStoryUser } from '../../../utils/fullstory';
import { handleUserRedirect } from '../../../utils/userRedirect';

import styles from './index.module.css';

const OtpRecoveryCodes = ({ recoveryCodes = [], user = null, onContinue, onDownload }) => {
  const history = useHistory();
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);

  const handleDownload = () => {
    const codesText = recoveryCodes.join('\n');
    const blob = new Blob(
      [
        `Two-Factor Authentication Recovery Codes\n\nIMPORTANT: Save these codes in a secure location.\nEach code can only be used once.\n\n${codesText}\n\nGenerated: ${new Date().toLocaleString()}`,
      ],
      { type: 'text/plain' }
    );

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setHasDownloaded(true);
    if (onDownload) onDownload();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setHasCopied(true);
    } catch (err) {
      console.error('Failed to copy recovery codes:', err);
    }
  };

  const handleContinue = () => {
    // Always handle redirect first, before calling onContinue which might clear state.
    if (user) {
      identifyFullStoryUser(user);
      handleUserRedirect(user, history, { isImpersonation: false });
    } else {
      history.push('/member/login');
    }

    if (onContinue) onContinue();
  };

  const canContinue = hasDownloaded || hasCopied;

  return (
    <div className={styles.auth_page}>
      <div className={styles.auth_page_content}>
        <div className={styles.auth_card}>
          <div className={styles.success_icon_wrap}>
            <svg width='28' height='28' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
              <polyline
                points='20 6 9 17 4 12'
                stroke='currentColor'
                strokeWidth='3'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>

          <div className={styles.auth_card_header}>
            <h1 className={styles.auth_heading}>Two-factor authentication enabled</h1>
            <p className={styles.auth_subtitle}>
              Save your recovery codes — you'll need them if you lose access to your authenticator app.
            </p>
          </div>

          <div className={styles.warning_callout}>
            <svg className={styles.warning_icon} viewBox='0 0 24 24' fill='none' aria-hidden='true'>
              <path
                d='M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <p className={styles.warning_text}>
              <strong>Each code can only be used once.</strong> Store them somewhere secure — a password manager works
              well.
            </p>
          </div>

          <p className={styles.codes_section_label}>Your recovery codes</p>
          <div className={`${styles.codes_grid} fs-mask`}>
            {recoveryCodes.map((code, index) => (
              <div key={index} className={styles.code_item}>
                <span className={styles.code_number}>{String(index + 1).padStart(2, '0')}.</span>
                <code className={styles.code_value}>{code}</code>
              </div>
            ))}
          </div>

          <div className={styles.action_row}>
            <button
              type='button'
              onClick={handleDownload}
              className={`${styles.auth_button_outline} ${hasDownloaded ? styles.is_active : ''}`}
            >
              {hasDownloaded ? 'Downloaded' : 'Download'}
            </button>
            <button
              type='button'
              onClick={handleCopy}
              className={`${styles.auth_button_outline} ${hasCopied ? styles.is_active : ''}`}
            >
              {hasCopied ? 'Copied' : 'Copy'}
            </button>
          </div>

          <div className={styles.security_note}>
            <p className={styles.security_note_title}>Security tips</p>
            <ul className={styles.security_note_list}>
              <li>Store these codes in a password manager.</li>
              <li>Print them and keep in a secure location.</li>
              <li>Never share these codes with anyone.</li>
            </ul>
          </div>

          <button type='button' onClick={handleContinue} className={styles.auth_button} disabled={!canContinue}>
            Finish setup
          </button>
        </div>
      </div>
    </div>
  );
};

export default OtpRecoveryCodes;
